import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Sprout, Globe, Store, TrendingUp, ArrowUpRight, Leaf, Coins } from 'lucide-react';
import { IMPACT_STATS, IMAGES } from '../data/mock';
import { getProjects } from '../lib/api';
import PlatformPreview from '../components/PlatformPreview';
import { useConnect } from '../context/ConnectContext';
import logo from '../assets/farmway-logo.png';

const pillars = [
  { icon: Globe, title: 'Global, curated projects', text: 'Farmland & agribusiness across 6 countries, sourced and operated in-house.' },
  { icon: Sprout, title: 'Real, regenerative assets', text: 'Productive land that compounds value while advancing 9 of the UN SDGs.' },
  { icon: Store, title: 'Liquidity via marketplace', text: 'A closed secondary market lets investors trade positions with confidence.' },
  { icon: ShieldCheck, title: 'Transparent ownership', text: 'Fractional, direct ownership with clear valuations and statements.' },
];

export default function InvestorOverview() {
  const { openConnect } = useConnect();
  const [projects, setProjects] = useState([]);
  useEffect(() => { getProjects().then((d) => setProjects(d.slice(0, 4))).catch(() => setProjects([])); }, []);

  return (
    <div className="min-h-screen bg-farmway-green">
      {/* HERO */}
      <section className="relative overflow-hidden pt-32 pb-16">
        <div className="absolute inset-0">
          <img src={IMAGES.heroAerial} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-farmway-green/85 to-farmway-green" />
        </div>
        <div className="container-fw relative">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Farmway" className="h-12 w-12 rounded-xl object-cover ring-1 ring-farmway-leaf/30" />
            <span className="font-display text-xl font-extrabold tracking-[0.2em] text-farmway-cream">FARMWAY</span>
          </div>
          <span className="eyebrow mt-8 block">Investor Overview</span>
          <h1 className="mt-4 max-w-3xl display-xl text-5xl text-farmway-cream sm:text-6xl">
            The investment platform for <span className="text-farmway-gold">future-proof farmland</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-farmway-cream/80">
            Farmway democratises access to sustainable farmland and agribusiness, combining real assets,
            in-house operations, and a closed secondary marketplace for liquidity.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <button onClick={openConnect} className="btn-gold">Talk to us <ArrowUpRight className="h-4 w-4" /></button>
            <Link to="/investment-opportunities" className="btn-outline">See opportunities</Link>
          </div>
        </div>
      </section>

      {/* METRICS */}
      <section className="border-y border-white/10 bg-farmway-green-dark/40">
        <div className="container-fw grid grid-cols-2 gap-px sm:grid-cols-4">
          {IMPACT_STATS.map((s) => (
            <div key={s.label} className="px-4 py-8 text-center">
              <p className="font-display text-3xl font-black text-farmway-gold sm:text-4xl">{s.value}</p>
              <p className="mt-2 text-xs uppercase tracking-wider text-farmway-cream/60">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT / WHY */}
      <section className="py-20">
        <div className="container-fw">
          <h2 className="max-w-2xl display-xl text-3xl text-farmway-cream sm:text-4xl">Why Farmway wins</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {pillars.map((p) => (
              <div key={p.title} className="rounded-2xl border border-white/10 bg-farmway-green-mid/40 p-6">
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-farmway-gold/15 text-farmway-gold"><p.icon className="h-6 w-6" /></div>
                <h3 className="mt-5 font-display text-lg font-bold text-farmway-cream">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-farmway-cream/70">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLATFORM + PROJECTS */}
      <section className="bg-farmway-green-dark py-20">
        <div className="container-fw grid items-center gap-16 lg:grid-cols-2">
          <div>
            <span className="eyebrow">The product</span>
            <h2 className="mt-4 display-xl text-3xl text-farmway-cream sm:text-4xl">A full-stack investing experience</h2>
            <ul className="mt-8 space-y-4">
              {['Discover & invest in curated projects', 'Track real holdings, value & returns', 'Trade positions on the secondary marketplace', 'Automated statements & alerts'].map((t) => (
                <li key={t} className="flex items-center gap-3 text-farmway-cream/85">
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-farmway-gold/15 text-farmway-gold"><Leaf className="h-3.5 w-3.5" /></span>{t}
                </li>
              ))}
            </ul>
          </div>
          <div className="flex justify-center"><PlatformPreview /></div>
        </div>
      </section>

      {/* PROJECT HIGHLIGHTS */}
      {projects.length > 0 && (
        <section className="py-20">
          <div className="container-fw">
            <h2 className="display-xl text-3xl text-farmway-cream sm:text-4xl">Portfolio highlights</h2>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {projects.map((p) => (
                <div key={p.id} className="overflow-hidden rounded-2xl border border-white/10 bg-farmway-green-mid/40">
                  <img src={p.image} alt={p.name} className="h-32 w-full object-cover" />
                  <div className="p-4">
                    <p className="font-display font-bold text-farmway-cream">{p.name}</p>
                    <p className="text-xs text-farmway-leaf-bright">{p.crop}</p>
                    <p className="mt-2 text-xs text-farmway-cream/50">{p.location}, {p.country}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative overflow-hidden py-24">
        <div className="absolute inset-0"><img src={IMAGES.goldenSunset} alt="" className="h-full w-full object-cover" /><div className="absolute inset-0 bg-farmway-green/90" /></div>
        <div className="container-fw relative text-center">
          <span className="eyebrow">Partner with Farmway</span>
          <h2 className="mx-auto mt-4 max-w-3xl display-xl text-4xl text-farmway-cream sm:text-5xl">Let&apos;s grow the future of food, together</h2>
          <button onClick={openConnect} className="btn-gold mx-auto mt-8"><Coins className="h-4 w-4" /> Request the investor deck</button>
          <p className="mt-6 font-display text-sm uppercase tracking-[0.3em] text-farmway-cream/50">Farm · Food · Future</p>
        </div>
      </section>
    </div>
  );
}
